import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Bot, Play, Pause, Search,
  ChevronRight,
} from 'lucide-react';
import { useAgentStore } from '@/store/agentStore';
import { formatRelativeTime } from '@/utils/format';
import { AgentStatus, AgentType } from '@/types';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';

const agentTypeLabels: Record<AgentType, string> = {
  sales: 'Sales',
  support: 'Support',
  onboarding: 'Onboarding',
  lead_generation: 'Lead Gen',
  document_collection: 'Doc Collection',
  custom: 'Custom',
};

const statusConfig: Record<AgentStatus, { label: string; dot: string; bg: string; text: string }> = {
  active: { label: 'Live', dot: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  testing: { label: 'Testing', dot: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
  paused: { label: 'Paused', dot: 'bg-orange-400', bg: 'bg-orange-50', text: 'text-orange-700' },
  draft: { label: 'Draft', dot: 'bg-gray-300', bg: 'bg-gray-50', text: 'text-gray-600' },
  archived: { label: 'Archived', dot: 'bg-red-400', bg: 'bg-red-50', text: 'text-red-700' },
};

const statusFilters: { value: AgentStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Live' },
  { value: 'testing', label: 'Testing' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
];

const typeFilters: { value: AgentType | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'custom', label: 'Custom' },
];

export const AgentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { agents, isLoading, fetchAgents, updateAgentStatus } = useAgentStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AgentStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<AgentType | 'all'>('all');

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleStatusToggle = async (e: React.MouseEvent, id: string, currentStatus: AgentStatus) => {
    e.stopPropagation();
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await updateAgentStatus(id, newStatus);
      toast.success(`Agent ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '';
      const errors: string[] = err?.response?.data?.details?.errors || [];
      if (errors.length > 0) {
        errors.forEach(e => toast.error(e));
      } else if (msg.toLowerCase().includes('greeting')) {
        toast.error('Add a greeting message before activating');
      } else if (msg.toLowerCase().includes('custom instructions')) {
        toast.error('Add custom instructions before activating');
      } else if (msg.toLowerCase().includes('knowledge')) {
        toast.error('Link a Knowledge Base before activating');
      } else if (msg.toLowerCase().includes('escalation')) {
        toast.error('Add an Escalation Rule before activating');
      } else if (msg.toLowerCase().includes('api key')) {
        toast.error('Configure an AI API key in Organization Settings');
      } else {
        toast.error(msg || 'Failed to update agent status');
      }
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    const matchesType = typeFilter === 'all' || agent.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const typeCountMap = agents.reduce<Record<string, number>>((acc, agent) => {
    acc[agent.type] = (acc[agent.type] || 0) + 1;
    return acc;
  }, {});

  if (isLoading && agents.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-7 w-32 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-48 bg-gray-100 rounded animate-pulse mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-5 h-44 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gray-100" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-32 bg-gray-100 rounded" />
                  <div className="h-3 w-20 bg-gray-50 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agents</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {agents.length} agent{agents.length !== 1 ? 's' : ''} in your workspace
          </p>
        </div>
        <button
          onClick={() => navigate('/agents/new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-violet-500 text-white text-sm font-medium rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-violet-600 active:bg-violet-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Agent
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] transition-all"
          />
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {statusFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                statusFilter === f.value
                  ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {typeFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                typeFilter === f.value
                  ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {f.label}
              <span className={cn(
                'ml-1 text-[10px]',
                typeFilter === f.value ? 'text-gray-500' : 'text-gray-400'
              )}>
                {f.value === 'all' ? agents.length : (typeCountMap[f.value] || 0)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Card Grid */}
      {filteredAgents.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Bot className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all' ? 'No agents found' : 'No agents yet'}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {searchQuery || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create your first AI agent to start automating conversations'}
            </p>
            {!searchQuery && statusFilter === 'all' && typeFilter === 'all' && (
              <button
                onClick={() => navigate('/agents/new')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500 text-white text-sm font-medium rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-violet-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Your First Agent
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredAgents.map((agent) => {
            const status = statusConfig[agent.status] || statusConfig.draft;
            return (
              <div
                key={agent.id}
                onClick={() => navigate(`/agents/${agent.id}`)}
                className="group bg-white rounded-xl border-2 border-[#1a1a2e]/80 p-5 cursor-pointer hover:border-violet-200 hover:shadow-[4px_4px_0px_#1a1a2e] transition-all duration-200"
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      agent.status === 'active' ? 'bg-emerald-100' :
                      agent.status === 'testing' ? 'bg-amber-100' : 'bg-gray-100'
                    )}>
                      <Bot className={cn(
                        'w-5 h-5',
                        agent.status === 'active' ? 'text-emerald-600' :
                        agent.status === 'testing' ? 'text-amber-600' : 'text-gray-400'
                      )} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-violet-600 transition-colors">
                        {agent.name}
                      </h3>
                      <p className="text-xs text-gray-500">{agentTypeLabels[agent.type]}</p>
                    </div>
                  </div>
                  <span className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium flex-shrink-0',
                    status.bg, status.text
                  )}>
                    <span className={cn('w-1.5 h-1.5 rounded-full', status.dot)} />
                    {status.label}
                  </span>
                </div>

                {/* Description */}
                <p className="text-xs text-gray-500 line-clamp-2 mb-4 min-h-[2rem]">
                  {agent.description || 'No description provided'}
                </p>

                {/* Bottom row */}
                <div className="flex items-center justify-between pt-3 border-t-2 border-[#1a1a2e]">
                  <span className="text-[11px] text-gray-400">
                    Updated {formatRelativeTime(agent.updated_at)}
                  </span>
                  <div className="flex items-center gap-1">
                    {agent.status !== 'archived' && (
                      <button
                        onClick={(e) => handleStatusToggle(e, agent.id, agent.status)}
                        className={cn(
                          'p-1.5 rounded-md transition-colors',
                          agent.status === 'active'
                            ? 'text-amber-600 hover:bg-amber-50'
                            : 'text-emerald-600 hover:bg-emerald-50'
                        )}
                        title={agent.status === 'active' ? 'Pause' : 'Activate'}
                      >
                        {agent.status === 'active' ? (
                          <Pause className="w-3.5 h-3.5" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-violet-400 transition-colors" />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Create new agent card */}
          <button
            onClick={() => navigate('/agents/new')}
            className="flex flex-col items-center justify-center bg-white rounded-xl border-2 border-dashed border-[#1a1a2e] p-5 min-h-[180px] hover:border-violet-300 hover:bg-violet-50/30 transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mb-2 group-hover:bg-violet-100 transition-colors">
              <Plus className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors" />
            </div>
            <p className="text-sm font-medium text-gray-600 group-hover:text-violet-700 transition-colors">Create New Agent</p>
            <p className="text-xs text-gray-400 mt-0.5">Use a template or start blank</p>
          </button>
        </div>
      )}
    </div>
  );
};
